import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from './services/apiService';

// Context Providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { PermissionsProvider } from './contexts/PermissionsContext';

// Components
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/UI/LoadingSpinner';
import Modal from './components/UI/Modal';
import { Calculator as CalculatorIcon } from 'lucide-react';
import Calculator from './components/UI/Calculator';
import Button from './components/UI/Button';

// Pages
import LoginPage from './pages/Auth/LoginPage';
import SetupWizard from './pages/Setup/SetupWizard';
import DashboardPage from './pages/Dashboard/DashboardPage';
import ProductsPage from './pages/Products/ProductsPage';
import InvoicesPage from './pages/Invoices/InvoicesPage';
import ProjectsPage from './pages/Projects/ProjectsPage';
import ReportsPage from './pages/Reports/ReportsPage';
import SettingsPage from './pages/Settings/SettingsPage';
import UsersPage from './pages/Users/UsersPage';
import NotFoundPage from './pages/NotFound/NotFoundPage';
import CustomersPage from './pages/Customers/CustomersPage';
import ProjectDetailPage from './pages/Projects/ProjectDetailPage';
import ProjectEditPage from './pages/Projects/ProjectEditPage';
import InvoiceDetailPage from './pages/Invoices/InvoiceDetailPage';
import EmployeesPage from './pages/EmployeesPage';
import SuppliersPage from './pages/SuppliersPage';
import PurchaseInvoicesPage from './pages/Invoices/PurchaseInvoicesPage';
import DeliveryInvoicesPage from './pages/Invoices/DeliveryInvoicesPage';
import ReturnInvoicesPage from './pages/Invoices/ReturnInvoicesPage';
import ProductCategoriesPage from './pages/Products/ProductCategoriesPage';
import LocationsPage from './pages/Products/LocationsPage';
import BrandsPage from './pages/Products/BrandsPage';
import TypesPage from './pages/Products/TypesPage';
import CompressorTypesPage from './pages/Products/CompressorTypesPage';
import CountriesPage from './pages/Products/CountriesPage';
import UnitsPage from './pages/Products/UnitsPage';
import WarrantyPage from './pages/Warranty/WarrantyPage';
import CSPortalWarrantyPage from './pages/Warranty/CSPortalWarrantyPage';
import RequisitionsPage from './pages/Requisitions/RequisitionsPage';
import PublicInvoiceViewPage from './pages/Invoices/PublicInvoiceViewPage';
import PublicDeliveryInvoiceViewPage from './pages/Invoices/PublicDeliveryInvoiceViewPage';
import PurchaseInvoicePrintView, { PurchaseInvoicePrintPreviewPage } from './pages/Invoices/PurchaseInvoicePrintView';
import DeliveryInvoicePrintView from './pages/Invoices/DeliveryInvoicePrintView';
import { DeliveryInvoice } from './pages/Invoices/DeliveryInvoicesPage';


interface CommonInvoiceItem {
  productId?: string;
  barcode?: string;
  name: string;
  unit?: string;
  qty: number;
  purchaseRate?: number; // For PurchaseInvoice
  price?: number; // For DeliveryInvoice
  amount: number;
  discount: number;
  total: number;
  brand?: string;
  model?: string;
  sku?: string;
  type?: string;
  compressorType?: string;
  countryOfOrigin?: string;
  countryOfAssembly?: string;
  details?: string;
  description?: string;
  capacity?: string;
  serialNo?: string;
  partNo?: string;
  quantity: number; // Added for DeliveryInvoice
  unitPrice: number; // Added for DeliveryInvoice
  totalPrice: number; // Added for DeliveryInvoice
}

interface BaseInvoice {
  _id: string;
  invoiceNumber: string;
  type: string; // Added for DeliveryInvoice
  customer?: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    contactPerson?: string; // Made optional for compatibility
  };
  items: CommonInvoiceItem[];
  status: string;
  createdAt: string;
  total: number;
  subtotal: number; // Added for DeliveryInvoice
  discount: number; // Added for DeliveryInvoice
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Setup Check Component
const SetupCheck: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [setupStatus, setSetupStatus] = useState<{ isSetup: boolean; loading: boolean }>({
    isSetup: false,
    loading: true
  });
  const { user } = useAuth();

  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await apiService.get('/setup/status');
        setSetupStatus({
          isSetup: response.data.isSetup,
          loading: false
        });
      } catch (error) {
        // If backend is unreachable, assume setup is complete and show login
        // This prevents showing setup wizard when backend is down
        setSetupStatus({
          isSetup: true,
          loading: false
        });
      }
    };
    checkSetup();
  }, [user]);

  useEffect(() => {
  }, [setupStatus]);

  if (setupStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <LoadingSpinner size="lg" color="primary" />
          <p className="mt-4 text-blue-600 text-lg font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  if (!setupStatus.isSetup) {
    return <SetupWizard />;
  }

  return <>{children}</>;
};

// Default redirect after login: requisition_user -> /requisitions, others -> /dashboard
const DefaultRedirect: React.FC = () => {
  const { user } = useAuth();
  const target = user?.role === 'requisition_user' ? '/requisitions' : '/dashboard';
  return <Navigate to={target} replace />;
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <LoadingSpinner size="lg" color="primary" />
          <p className="mt-4 text-blue-600 text-lg font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirects to dashboard or requisitions if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <LoadingSpinner size="lg" color="primary" />
          <p className="mt-4 text-blue-600 text-lg font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === 'requisition_user' ? '/requisitions' : '/dashboard'} replace />;
  }

  return <>{children}</>;
};

// Main App Component
const App: React.FC = () => {
  // Print state at root
  const [showPrintView, setShowPrintView] = useState(false);
  const [printInvoice, setPrintInvoice] = useState<BaseInvoice | null>(null);
  const [printType, setPrintType] = useState<'purchase' | 'delivery' | null>(null);

  // Print trigger function to pass to children
  const handlePrint = (invoice: BaseInvoice, type: 'purchase' | 'delivery') => {
    setPrintInvoice(invoice);
    setPrintType(type);
    setShowPrintView(true);
    setTimeout(() => {
      // Hide everything except #print-root
      const appRoot = document.getElementById('root');
      const printRoot = document.getElementById('print-root');
      const hiddenSiblings: HTMLElement[] = [];
      if (appRoot && printRoot) {
        for (const child of Array.from(appRoot.children)) {
          if (child !== printRoot) {
            (child as HTMLElement).style.display = 'none';
            hiddenSiblings.push(child as HTMLElement);
          }
        }
      }
      window.print();
      // Restore after print
      setTimeout(() => {
        for (const el of hiddenSiblings) {
          el.style.display = '';
        }
      }, 1000);
    }, 100);
  };

  // Hide print view after printing
  useEffect(() => {
    if (!showPrintView) return;
    const onAfterPrint = () => setShowPrintView(false);
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, [showPrintView]);

  useEffect(() => {
    if (!showPrintView) setPrintInvoice(null);
  }, [showPrintView]);

  // Calculator state
  const [calculatorOpen, setCalculatorOpen] = useState(false);

  return (
    <QueryClientProvider client={queryClient}>
      <SettingsProvider>
        <ThemeProvider>
          <AuthProvider>
            <PermissionsProvider>
              <Router>
              {/* Print view at root, hide rest of app during print */}
              {showPrintView && printInvoice && (
                <div id="print-root" style={{ display: 'block' } as any}>
                  {printType === 'purchase' && printInvoice && (
                    <PurchaseInvoicePrintView invoice={printInvoice} settings={{}} currentPage={1} itemsPerPage={1} printMode={true} />
                  )}
                  {printType === 'delivery' && printInvoice && (
                    <DeliveryInvoicePrintView invoice={printInvoice as DeliveryInvoice} settings={{}} currentPage={1} itemsPerPage={1} printMode={true} />
                  )}
                </div>
              )}
              {/* Floating Calculator Button */}
              <Button
                onClick={() => setCalculatorOpen(true)}
                className="fixed z-[9999] bottom-6 right-6 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white rounded-full shadow-lg p-4 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                style={{ boxShadow: '0 4px 24px 0 rgba(0,0,0,0.15)' }}
                aria-label="Open Calculator"
              >
                <CalculatorIcon className="w-7 h-7" />
              </Button>
              {/* Calculator Modal */}
              <Modal
                isOpen={calculatorOpen}
                onClose={() => setCalculatorOpen(false)}
                title="Calculator"
                size="sm"
                showCloseButton
                className="z-[10000]"
              >
                <Calculator />
              </Modal>
              <div className={showPrintView ? 'print:hidden' : ''}>
                <AnimatePresence mode="wait">
                  <Routes>
                    {/* Public Routes */}
                    <Route
                      path="/login"
                      element={
                        <PublicRoute>
                          <motion.div
                            initial={{ opacity: 0, y: 20 } as any}
                            animate={{ opacity: 1, y: 0 } as any}
                            exit={{ opacity: 0, y: -20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <LoginPage />
                          </motion.div>
                        </PublicRoute>
                      }
                    />
                    <Route
                      path="/invoices/:id/public-view"
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 } as any}
                          animate={{ opacity: 1, y: 0 } as any}
                          exit={{ opacity: 0, y: -20 } as any}
                          transition={{ duration: 0.3 }}
                        >
                          <PublicInvoiceViewPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="/delivery-invoices/:id/public-view"
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 } as any}
                          animate={{ opacity: 1, y: 0 } as any}
                          exit={{ opacity: 0, y: -20 } as any}
                          transition={{ duration: 0.3 }}
                        >
                          <PublicDeliveryInvoiceViewPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="/CSPortal/Warranty/:serialNo"
                      element={
                        <motion.div
                          initial={{ opacity: 0, y: 20 } as any}
                          animate={{ opacity: 1, y: 0 } as any}
                          exit={{ opacity: 0, y: -20 } as any}
                          transition={{ duration: 0.3 }}
                        >
                          <CSPortalWarrantyPage />
                        </motion.div>
                      }
                    />
                    <Route path="/print/purchase-invoice/:id" element={<PurchaseInvoicePrintPreviewPage />} />
                    <Route path="/invoices/:id/print-preview" element={<PurchaseInvoicePrintPreviewPage />} />

                    {/* Protected Routes with Layout */}
                    <Route
                      path="/"
                      element={
                        <SetupCheck>
                          <ProtectedRoute>
                            <Layout />
                          </ProtectedRoute>
                        </SetupCheck>
                      }
                    >
                      <Route index element={<DefaultRedirect />} />
                      <Route
                        path="dashboard"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <DashboardPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="products"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <ProductsPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="invoices"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <InvoicesPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="invoices/:id"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <InvoiceDetailPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="invoices/purchase"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <PurchaseInvoicesPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="invoices/delivery"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <DeliveryInvoicesPage handlePrint={handlePrint} />
                          </motion.div>
                        }
                      />
                      <Route
                        path="invoices/return"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <ReturnInvoicesPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="projects"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <ProjectsPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="projects/:id"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <ProjectDetailPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="projects/:id/edit"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <ProjectEditPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="reports"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <ReportsPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="warranty"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <WarrantyPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="requisitions"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <RequisitionsPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="settings"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <SettingsPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="users"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <UsersPage />
                          </motion.div>
                        }
                      />
                      <Route path="profile" element={<Navigate to="/users" replace />} />

                      <Route
                        path="customers"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <CustomersPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="customers"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <CustomersPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="employees"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <EmployeesPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="suppliers"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <SuppliersPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="product-categories"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <ProductCategoriesPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="locations"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <LocationsPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="brands"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <BrandsPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="types"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <TypesPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="compressor-types"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <CompressorTypesPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="countries"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <CountriesPage />
                          </motion.div>
                        }
                      />
                      <Route
                        path="units"
                        element={
                          <motion.div
                            initial={{ opacity: 0, x: -20 } as any}
                            animate={{ opacity: 1, x: 0 } as any}
                            exit={{ opacity: 0, x: 20 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <UnitsPage />
                          </motion.div>
                        }
                      />

                    </Route>

                    {/* Catch-all route - redirect to login if not authenticated, otherwise show 404 */}
                    <Route
                      path="*"
                      element={
                        <ProtectedRoute>
                          <motion.div
                            initial={{ opacity: 0 } as any}
                            animate={{ opacity: 1 } as any}
                            exit={{ opacity: 0 } as any}
                            transition={{ duration: 0.3 }}
                          >
                            <NotFoundPage />
                          </motion.div>
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </AnimatePresence>

                {/* Toast Notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      backgroundColor: '#0F0F0F',
                      color: '#FFFFFF',
                      fontFamily: 'Times New Roman, serif',
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: '#10B981',
                        secondary: 'var(--color-text)',
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: '#EF4444',
                        secondary: 'var(--color-text)',
                      },
                    },
                  }}
                />
              </div>
                          </Router>
            </PermissionsProvider>
          </AuthProvider>
        </ThemeProvider>
      </SettingsProvider>
    </QueryClientProvider>
  );
};

export default App; 